import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const FAQSection = ({ sidebar = null }) => {
  const { t } = useTranslation();

  const faqs = ['q1', 'q2', 'q3', 'q4', 'q5'].map((key) => ({
    key,
    question: t(`faq.${key}.question`),
    answer: t(`faq.${key}.answer`),
    linkLabel: t(`faq.${key}.linkLabel`, { defaultValue: '' }),
    linkTo: t(`faq.${key}.linkTo`, { defaultValue: '' })
  }));

  // When a sidebar slot is provided (e.g. the Honor Wall collage on the home
  // page), the layout shifts from a centered single column to a 2-column
  // grid: FAQ accordion on the left, sidebar on the right at lg+ widths.
  const hasSidebar = Boolean(sidebar);
  const containerCls = hasSidebar
    ? 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'
    : 'max-w-4xl mx-auto px-4 sm:px-6 lg:px-8';

  return (
    <section className="py-20 bg-gray-50">
      <div className={containerCls}>
        {!hasSidebar && (
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('faq.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('faq.subtitle')}
            </p>
          </div>
        )}

        <div className={hasSidebar ? 'grid grid-cols-1 lg:grid-cols-2 gap-10 items-start' : ''}>
          <div>
            {hasSidebar && (
              <div className="mb-8">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
                  {t('faq.title')}
                </h2>
                <p className="text-base text-gray-600">
                  {t('faq.subtitle')}
                </p>
              </div>
            )}
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq) => (
                <AccordionItem
                  key={faq.key}
                  value={faq.key}
                  className="bg-white px-6 rounded-lg border border-gray-100 shadow-sm"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed pb-5">
                    <p>{faq.answer}</p>
                    {faq.linkTo && faq.linkLabel ? (
                      <Link
                        to={faq.linkTo}
                        className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-[#1e3a8a] hover:text-[#1e40af] transition-colors group"
                        data-testid={`faq-${faq.key}-link`}
                      >
                        {faq.linkLabel}
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                      </Link>
                    ) : null}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          {hasSidebar && (
            <div className="lg:sticky lg:top-24" data-testid="faq-sidebar-slot">
              {sidebar}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
